// ===============================================================================
// TermsModal.js - Terms and Conditions Modal for SplitMon
// ===============================================================================
// Location: /var/www/wow/frontend/src/TermsModal.js
// Displays complete Terms and Conditions in a scrollable modal
// ===============================================================================

import React from "react";
import "./TermsModal.css";

function TermsModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="terms-modal-overlay" onClick={onClose}>
      <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
        <div className="terms-modal-header">
          <h2>Terms and Conditions</h2>
          <button className="terms-close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="terms-modal-content">
          <p className="terms-updated">Last Updated: January 2026</p>
          
          <p>
            These Terms and Conditions ("Terms") constitute a legally binding agreement between you 
            (the "User") and <strong>CSTN Networks LTD</strong>, a company incorporated in Cyprus 
            (Registration No. HE 358481), having its registered office at 105 Griva Digeni Street, 
            Steides Court 3rd Floor, Office 301, Linopetra 3107, Limassol, Cyprus ("Company", "we", 
            "us", or "our").
          </p>
          
          <p>
            SplitMon is an expense-tracking and expense-sharing software application (the "Service").
          </p>
          
          <p>
            By creating an account, accessing, or using the Service, you confirm that you have read, 
            understood, and agree to be bound by these Terms. <strong>If you do not agree, you must 
            not use the Service.</strong>
          </p>

          {/* ================================================================= */}
          {/* SECTION 1 */}
          {/* ================================================================= */}
          <h3>1. Purpose and Nature of the Service</h3>
          <p>
            SplitMon is provided strictly as a <strong>tracking and informational tool only</strong>.
          </p>
          <p><strong>The Service does NOT:</strong></p>
          <ul>
            <li>Transfer, process, hold, verify, escrow, or facilitate real money or payments;</li>
            <li>Execute, validate, or enforce payments or settlements between users;</li>
            <li>Act as a bank, payment institution, accountant, bookkeeper, or financial intermediary;</li>
            <li>Provide financial, legal, tax, accounting, or professional advice of any kind.</li>
          </ul>
          <p>
            <strong>All data, balances, calculations, and records displayed within the Service are 
            indicative only and are provided for informational purposes.</strong> Users are solely 
            responsible for verifying accuracy and settling any obligations outside the Service.
          </p>
          <p>
            For financial, legal, or tax matters, you should consult qualified professionals. The 
            Company accepts no responsibility for decisions made based on information displayed in 
            the Service.
          </p>

          {/* ================================================================= */}
          {/* SECTION 2 */}
          {/* ================================================================= */}
          <h3>2. Eligibility and Age Requirement</h3>
          <p>
            The Service is intended exclusively for individuals who are <strong>at least eighteen (18) 
            years old</strong> and have the legal capacity to enter into binding contracts.
          </p>
          <p>
            By using the Service, you represent and warrant that you meet this age requirement and 
            are legally permitted to use the Service under applicable laws. We do not verify age 
            and accept no liability for misrepresentation.
          </p>

          {/* ================================================================= */}
          {/* SECTION 3 */}
          {/* ================================================================= */}
          <h3>3. Account Registration and Security</h3>
          <p>
            You must provide a valid email address for authentication purposes.
          </p>
          <p>
            You are responsible for safeguarding your login credentials and for all activities 
            occurring under your account. We are not liable for unauthorized access resulting 
            from your failure to protect your account credentials.
          </p>

          {/* ================================================================= */}
          {/* SECTION 4 */}
          {/* ================================================================= */}
          <h3>4. Free (Basic) and Premium Accounts</h3>
          
          <h4>4.1 Basic Account</h4>
          <p>Free Basic accounts allow:</p>
          <ul>
            <li>Creation or participation in up to two (2) basic groups;</li>
            <li>A limited number of expense records (up to 200 records per user);</li>
            <li>Core expense tracking and balance calculation features.</li>
          </ul>

          <h4>4.2 Premium Account</h4>
          <p>Premium subscriptions provide expanded features, including but not limited to:</p>
          <ul>
            <li>Unlimited groups and expense records;</li>
            <li>Advanced group templates (including Business, Construction, and Loans group types);</li>
            <li>Advanced analytics and reporting;</li>
            <li>Receipt attachments;</li>
            <li>Ad-free experience.</li>
          </ul>

          {/* ================================================================= */}
          {/* SECTION 5 */}
          {/* ================================================================= */}
          <h3>5. Business, Construction, and Specialized Groups Disclaimer</h3>
          <p>
            Specialized group types (including Business, Construction, or Loans groups) are provided 
            <strong> solely for organizational and informational convenience</strong>.
          </p>
          <p><strong>They do NOT:</strong></p>
          <ul>
            <li>Represent real accounting, bookkeeping, or financial records;</li>
            <li>Comply with accounting, tax, payroll, or financial reporting standards;</li>
            <li>Substitute for professional accounting or financial software.</li>
          </ul>
          <p>
            These groups must <strong>not be relied upon</strong> for legal, tax, payroll, invoicing, 
            or regulatory purposes. The Company bears <strong>no responsibility or liability</strong> for 
            any decisions, actions, losses, or disputes arising from the use of such groups.
          </p>

          {/* ================================================================= */}
          {/* SECTION 6 */}
          {/* ================================================================= */}
          <h3>6. Trial Period</h3>
          <p>
            At our sole discretion, we may offer a free Premium trial period.
          </p>
          <p>
            At the end of any trial period, accounts automatically revert to Basic unless a paid 
            subscription is purchased. No charges will occur unless the User actively subscribes 
            to Premium.
          </p>
          <p>
            We reserve the right to modify or discontinue trial offerings at any time without notice.
          </p>

          {/* ================================================================= */}
          {/* SECTION 7 */}
          {/* ================================================================= */}
          <h3>7. Subscriptions, Pricing, and Payments</h3>

          <h4>7.1 Pricing</h4>
          <p>
            Current subscription pricing is published within the Service and on our website. 
            Prices are exclusive of VAT.
          </p>
          <p>
            Applicable VAT, if any, is determined based on EU VAT rules and the User's country 
            of residence at the time of purchase.
          </p>
          <p>
            We reserve the right to modify pricing at any time. Price changes will not affect 
            active subscription periods.
          </p>

          <h4>7.2 Payment Processing</h4>
          <p>
            Payments are currently processed via PayPal. Additional payment methods may be 
            introduced in the future.
          </p>
          <p>
            The Company does not store full payment card details.
          </p>

          {/* ================================================================= */}
          {/* SECTION 8 */}
          {/* ================================================================= */}
          <h3>8. No Refund Policy</h3>
          <p>
            Due to the availability of an extended free trial period allowing full evaluation 
            of the Service, <strong>ALL SUBSCRIPTION PAYMENTS ARE FINAL AND NON-REFUNDABLE.</strong>
          </p>
          <p>
            By purchasing a subscription and requesting immediate access to the Service, you expressly:
          </p>
          <ul>
            <li>Consent to the immediate performance of the contract;</li>
            <li>Acknowledge that you lose your right of withdrawal once the Service is fully provided;</li>
            <li>Waive any statutory right of withdrawal to the maximum extent permitted by applicable 
                law, in accordance with Article 16(m) of Directive 2011/83/EU.</li>
          </ul>

          {/* ================================================================= */}
          {/* SECTION 9 */}
          {/* ================================================================= */}
          <h3>9. Accuracy and Calculation Disclaimer</h3>
          <p>
            While we strive to provide accurate calculations, <strong>you acknowledge and agree that:</strong>
          </p>
          <ul>
            <li>The Service may contain bugs, errors, rounding differences, or inaccuracies;</li>
            <li>Currency conversions are indicative only and may not reflect real-time exchange rates;</li>
            <li>All records are user-generated and are not verified by us;</li>
            <li>Calculations are for informational purposes only and should not be relied upon as 
                definitive financial records.</li>
          </ul>
          <p>
            <strong>You are solely responsible for verifying all data, calculations, and amounts 
            with all parties involved before settling any debts or making financial decisions.</strong>
          </p>

          {/* ================================================================= */}
          {/* SECTION 10 */}
          {/* ================================================================= */}
          <h3>10. User Responsibilities and Prohibited Use</h3>
          <p>
            You agree to use the Service only for lawful purposes and in accordance with these Terms.
          </p>
          <p><strong>You must not:</strong></p>
          <ul>
            <li>Use the Service for any illegal or fraudulent purpose;</li>
            <li>Record knowingly false, misleading, or fraudulent data;</li>
            <li>Attempt to access other users' accounts or data;</li>
            <li>Interfere with system security, integrity, or operation;</li>
            <li>Upload malicious code, viruses, or harmful content;</li>
            <li>Violate the privacy or rights of other users.</li>
          </ul>

          {/* ================================================================= */}
          {/* SECTION 11 */}
          {/* ================================================================= */}
          <h3>11. Data Protection and Privacy (GDPR)</h3>
          <p>
            The Company acts as Data Controller under Regulation (EU) 2016/679 (GDPR).
          </p>
          <p>Personal data is processed strictly for:</p>
          <ul>
            <li>Service provision and operation;</li>
            <li>Security and fraud prevention;</li>
            <li>System improvement and maintenance.</li>
          </ul>
          <p>
            Data may be processed on servers located within the European Union.
          </p>

          <h4>11.1 Data Security</h4>
          <p>
            We implement appropriate technical and organizational security measures to protect 
            your personal data, including:
          </p>
          <ul>
            <li>Encryption of data in transit and at rest;</li>
            <li>Secure password hashing using industry-standard algorithms;</li>
            <li>Access controls and authentication mechanisms;</li>
            <li>Regular security monitoring and logging.</li>
          </ul>
          <p>
            No method of electronic transmission or storage is completely secure. While we strive 
            to protect your personal data, we cannot guarantee absolute security.
          </p>

          <h4>11.2 Your Data Rights Under GDPR</h4>
          <p>As a User, you have the following rights regarding your personal data:</p>
          <ul>
            <li><strong>Right of Access:</strong> Request a copy of the personal data we hold about you;</li>
            <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete personal data;</li>
            <li><strong>Right to Erasure:</strong> Request deletion of your personal data, subject to legal retention requirements;</li>
            <li><strong>Right to Data Portability:</strong> Receive your personal data in a structured, commonly used, machine-readable format;</li>
            <li><strong>Right to Restrict Processing:</strong> Request limitation of processing in certain circumstances;</li>
            <li><strong>Right to Object:</strong> Object to certain types of data processing;</li>
            <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local data protection supervisory authority.</li>
          </ul>
          <p>
            To exercise any of these rights, contact us at <strong>splitmon@cstnapps.com</strong> with 
            the subject line "Data Protection Request". We will respond within thirty (30) days, 
            subject to identity verification and legal requirements.
          </p>

          <h4>11.3 Data Retention</h4>
          <p>
            We retain your personal data for as long as your account remains active.
          </p>
          <p>
            Upon account deletion, your personal data will be anonymized or permanently deleted 
            within <strong>twelve (12) days</strong>, unless retention is required for legal, security, 
            or legitimate business purposes.
          </p>
          <p>
            Backups are retained for a limited operational period and are not intended for 
            user-requested data restoration. Users may export their data at any time via 
            available export features within the Service.
          </p>

          {/* ================================================================= */}
          {/* SECTION 12 */}
          {/* ================================================================= */}
          <h3>12. Intellectual Property</h3>
          <p>
            All intellectual property rights in the Service, including but not limited to software, 
            design, logos, trademarks, and content, belong exclusively to CSTN Networks LTD.
          </p>
          <p>
            You receive a limited, non-exclusive, non-transferable, revocable license to use the 
            Service for personal purposes in accordance with these Terms.
          </p>
          <p><strong>You may not:</strong></p>
          <ul>
            <li>Copy, modify, or distribute any part of the Service;</li>
            <li>Reverse engineer or attempt to extract source code;</li>
            <li>Remove any proprietary notices or labels;</li>
            <li>Use the Service for any commercial purpose without prior written authorization.</li>
          </ul>

          {/* ================================================================= */}
          {/* SECTION 13 */}
          {/* ================================================================= */}
          <h3>13. Limitation of Liability</h3>
          <p>
            <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,</strong> either 
            express or implied, including but not limited to implied warranties of merchantability, 
            fitness for a particular purpose, non-infringement, or course of performance.
          </p>
          <p><strong>To the maximum extent permitted by applicable law:</strong></p>
          <ul>
            <li>We disclaim all warranties, express or implied;</li>
            <li>We are not liable for any indirect, incidental, special, consequential, or punitive damages;</li>
            <li>Our total liability shall not exceed the amount actually paid by you for the Service 
                in the twelve (12) months immediately preceding the claim, or zero (€0) for free users.</li>
          </ul>
          <p>
            Nothing in these Terms excludes or limits liability that cannot be excluded or limited 
            under applicable law.
          </p>

          <h4>13.1 Force Majeure</h4>
          <p>
            We shall not be liable for any failure or delay in performing our obligations where 
            such failure or delay results from circumstances beyond our reasonable control, 
            including but not limited to:
          </p>
          <ul>
            <li>Natural disasters, acts of God, floods, earthquakes, or severe weather;</li>
            <li>War, terrorism, civil unrest, or armed conflict;</li>
            <li>Government actions, sanctions, or embargoes;</li>
            <li>Pandemics, epidemics, or public health emergencies;</li>
            <li>Power outages, telecommunications failures, or internet service disruptions;</li>
            <li>Cyberattacks, hacking, or distributed denial-of-service attacks;</li>
            <li>Failure of third-party service providers or infrastructure.</li>
          </ul>
          <p>
            During such events, our obligations shall be suspended for the duration of the circumstances.
          </p>

          {/* ================================================================= */}
          {/* SECTION 14 */}
          {/* ================================================================= */}
          <h3>14. Termination</h3>

          <h4>14.1 Termination for Cause</h4>
          <p>
            We may suspend or terminate your account immediately and without notice for misuse, 
            abuse, fraud, or violation of these Terms. No refunds apply in such cases.
          </p>

          <h4>14.2 Termination Without Cause</h4>
          <p>
            If we terminate a paid subscription without cause, a pro-rata refund for the unused 
            subscription period will be issued within fourteen (14) days.
          </p>

          <h4>14.3 Account Deletion by User</h4>
          <p>
            You may delete your account at any time through the Settings page within the Service.
          </p>
          <p>Upon initiating deletion:</p>
          <ul>
            <li>Your account will be scheduled for permanent deletion after a <strong>ten (10) day 
                grace period</strong>;</li>
            <li><strong>During this grace period, you may recover your account by logging in</strong>;</li>
            <li>After the grace period expires, deletion is <strong>permanent and irreversible</strong>;</li>
            <li>You will be removed from all groups you are a member of;</li>
            <li>Groups where you are the sole member will be permanently deleted;</li>
            <li>If you are the administrator of a group with other members, you must transfer 
                admin rights before deletion.</li>
          </ul>
          <p><em>We strongly recommend exporting your data before initiating account deletion.</em></p>

          {/* ================================================================= */}
          {/* SECTION 15 */}
          {/* ================================================================= */}
          <h3>15. Indemnification</h3>
          <p>
            You agree to indemnify, defend, and hold harmless CSTN Networks LTD, its directors, 
            officers, employees, agents, and affiliates from and against any and all claims, 
            damages, losses, liabilities, costs, and expenses (including reasonable legal fees) 
            arising out of or related to:
          </p>
          <ul>
            <li>Your use or misuse of the Service;</li>
            <li>Your violation of these Terms;</li>
            <li>Your violation of any applicable law or third-party rights;</li>
            <li>Any content or data you submit to the Service;</li>
            <li>Any dispute between you and other users regarding expenses, balances, or settlements.</li>
          </ul>
          <p>
            <strong>This indemnification obligation shall survive the termination of your account 
            and these Terms.</strong>
          </p>

          {/* ================================================================= */}
          {/* SECTION 16 */}
          {/* ================================================================= */}
          <h3>16. Service Availability and Modifications</h3>

          <h4>16.1 Service Availability</h4>
          <p>
            The Service may be temporarily unavailable due to maintenance, upgrades, or third-party 
            provider outages.
          </p>
          <p>
            We do not guarantee uninterrupted or error-free availability and are not liable for 
            any downtime or service interruptions.
          </p>

          <h4>16.2 Modifications to Service</h4>
          <p>
            We reserve the right to modify, suspend, or discontinue any part of the Service at 
            any time, with or without notice.
          </p>
          <p>
            We are not liable to you or any third party for any modification, suspension, or 
            discontinuation of the Service.
          </p>

          <h4>16.3 Third-Party Services</h4>
          <p>
            The Service may rely on third-party providers for hosting, payment processing, and 
            other functionality. <strong>The Company is not responsible for failures, interruptions, 
            or errors caused by third-party services.</strong>
          </p>

          {/* ================================================================= */}
          {/* SECTION 17 */}
          {/* ================================================================= */}
          <h3>17. Governing Law and Jurisdiction</h3>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the 
            Republic of Cyprus.
          </p>
          <p>
            The courts of Cyprus shall have exclusive jurisdiction over any disputes arising out 
            of or relating to these Terms, without prejudice to mandatory EU consumer protections 
            applicable in your country of residence.
          </p>

          {/* ================================================================= */}
          {/* SECTION 18 */}
          {/* ================================================================= */}
          <h3>18. EU Online Dispute Resolution</h3>
          <p>
            Consumers in the European Union may access the Online Dispute Resolution platform 
            provided by the European Commission at:{" "}
            <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
              https://ec.europa.eu/consumers/odr
            </a>
          </p>

          {/* ================================================================= */}
          {/* SECTION 19 */}
          {/* ================================================================= */}
          <h3>19. Changes to These Terms</h3>
          <p>
            We reserve the right to modify these Terms at any time. We will provide notice of 
            material changes by posting the updated Terms within the Service and updating the 
            "Last Updated" date.
          </p>
          <p>
            Your continued use of the Service after such changes constitutes your acceptance of 
            the revised Terms. If you do not agree to the revised Terms, you must stop using 
            the Service.
          </p>

          {/* ================================================================= */}
          {/* SECTION 20 */}
          {/* ================================================================= */}
          <h3>20. General Provisions</h3>

          <h4>20.1 Entire Agreement</h4>
          <p>
            These Terms, together with any applicable Privacy Policy, constitute the entire 
            agreement between you and the Company regarding the Service and supersede all prior 
            agreements and understandings.
          </p>

          <h4>20.2 Severability</h4>
          <p>
            If any provision of these Terms is found to be invalid, illegal, or unenforceable by 
            a court of competent jurisdiction, the remaining provisions shall continue in full 
            force and effect.
          </p>

          <h4>20.3 No Waiver</h4>
          <p>
            Our failure to enforce any right or provision of these Terms shall not constitute a 
            waiver of that right or provision. Any waiver must be in writing and signed by an 
            authorized representative of the Company.
          </p>

          <h4>20.4 Assignment</h4>
          <p>
            You may not assign or transfer your rights or obligations under these Terms without 
            our prior written consent. We may assign our rights and obligations without restriction.
          </p>

          <h4>20.5 No Partnership or Agency</h4>
          <p>
            Nothing in these Terms creates a partnership, joint venture, agency, or fiduciary 
            relationship between you and the Company. Neither party has the authority to bind 
            the other or incur obligations on the other's behalf.
          </p>

          <h4>20.6 Headings</h4>
          <p>
            The section headings in these Terms are for convenience only and have no legal or 
            contractual effect.
          </p>

          {/* ================================================================= */}
          {/* SECTION 21 */}
          {/* ================================================================= */}
          <h3>21. Contact Information</h3>
          <p>
            <strong>CSTN Networks LTD</strong><br />
            Registration No.: HE 358481<br />
            105 Griva Digeni Street<br />
            Steides Court 3rd Floor, Office 301<br />
            Linopetra 3107, Limassol<br />
            Cyprus<br />
            Email: splitmon@cstnapps.com
          </p>
          <p>
            <em>Note: These Terms and Conditions and the registered office address may be updated 
            from time to time. Please refer to the Service for the most current version.</em>
          </p>

          {/* ================================================================= */}
          {/* ACKNOWLEDGMENT */}
          {/* ================================================================= */}
          <div className="terms-acknowledgment">
            <p>
              <strong>BY USING THE SERVICE, YOU CONFIRM THAT YOU HAVE READ, UNDERSTOOD, AND AGREED 
              TO THESE TERMS AND CONDITIONS IN FULL.</strong>
            </p>
          </div>

          <p>You specifically acknowledge and agree that:</p>
          <ul>
            <li>The Service is a tracking tool only and does not handle real money;</li>
            <li>The Service may contain errors and all calculations should be independently verified;</li>
            <li>You are solely responsible for resolving disputes with other users;</li>
            <li>The Service does not provide financial, legal, or tax advice;</li>
            <li>Your use of the Service is entirely at your own risk.</li>
          </ul>

          <p className="terms-footer">
            © 2026 CSTN Networks LTD. All Rights Reserved.<br />
            SplitMon is a registered product of CSTN Networks LTD
          </p>
        </div>
        
        <div className="terms-modal-footer">
          <button className="terms-accept-btn" onClick={onClose}>
            I Agree
          </button>
        </div>
      </div>
    </div>
  );
}

export default TermsModal;

